/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import makeStyles from "@material-ui/core/styles/makeStyles";
import clsx from "clsx";
import React from "react";

interface CircularProgressProps {
    classes?: Record<string, unknown>;
    className?: string;
    color?: "primary" | "secondary" | "inherit";
    size?: number | string;
    thickness?: number;
}

const SIZE = 44;

const useClasses = makeStyles((theme) => ({
    "@keyframes rotate": {
        "100%": {
            transform: "rotate(360deg)",
        },
    },
    circle: {
        strokeDasharray: "65px, 70px",
        strokeDashoffset: "-10px",
    },
    colorPrimary: {
        color: theme.palette.primary.main,
    },
    colorSecondary: {
        color: theme.palette.secondary.main,
    },
    root: {
        animation: "$rotate 1.4s linear infinite",
        backfaceVisibility: "hidden",
        display: "inline-block",
    },
    svgGradientId: {}, // Used as dynamic ID
}));

export default function CircularProgress(props: CircularProgressProps) {
    const { className, color = "primary", size = 40, thickness = 3.6 } = props;
    const classes = useClasses();

    return (
        <div
            className={clsx(
                classes.root,
                {
                    [classes.colorPrimary]: color === "primary",
                    [classes.colorSecondary]: color === "secondary",
                },
                className
            )}
            style={{ borderWidth: thickness, height: size, width: size }}
        >
            <svg viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}>
                <defs>
                    <linearGradient id={classes.svgGradientId} x1="75%" y1="75%" x2="19%" y2="19%">
                        <stop offset="0%" stopColor="currentColor" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <circle
                    stroke={`url(#${classes.svgGradientId})`}
                    cx={SIZE}
                    cy={SIZE}
                    r={(SIZE - thickness) / 2}
                    fill="none"
                    strokeWidth={thickness}
                />
                <circle
                    className={classes.circle}
                    stroke="currentColor"
                    cx={SIZE}
                    cy={SIZE}
                    r={(SIZE - thickness) / 2}
                    fill="none"
                    strokeWidth={thickness}
                />
            </svg>
        </div>
    );
}
